import { detectCurrency, DEFAULT_CURRENCY } from '@/services/currency/currencyDetector'
import { fetchUsdRates } from '@/services/currency/exchangeRateProvider'
import { readCache, writeCache, isFresh } from '@/services/currency/rateCache'
import { formatCurrency, formatCurrencyParts } from '@/services/currency/currencyFormatter'

// Single-flight guard: concurrent initialize() calls share one network request
let inflightFetch = null

export default {
  namespaced: true,
  state: {
    currencyCode: DEFAULT_CURRENCY,
    country: null,
    timezone: null,
    rates: null, // { PKR: 277.9, AED: 3.67, ... } — always USD-based
    status: 'idle', // idle | loading | ready | error
    ratesUpdatedAt: null,
    provider: null,
  },
  mutations: {
    SET_DETECTION(state, { timezone, country, currency }) {
      state.timezone = timezone
      state.country = country
      state.currencyCode = currency
    },
    SET_CURRENCY(state, currencyCode) {
      state.currencyCode = currencyCode
    },
    SET_RATES(state, { rates, fetchedAt, provider }) {
      state.rates = rates
      state.ratesUpdatedAt = fetchedAt
      state.provider = provider
    },
    SET_STATUS(state, status) {
      state.status = status
    },
  },
  getters: {
    currencyCode: state => state.currencyCode,
    /** True once rates are loaded and prices can be shown in the local currency. */
    isReady: state => state.status === 'ready' && Boolean(state.rates),
    /**
     * USD → target rate, or null when unavailable (rates not loaded yet,
     * or the currency is missing from the provider's table).
     */
    rate: state => (currencyCode = state.currencyCode) => {
      if (currencyCode === 'USD') return 1
      const rate = state.rates && state.rates[currencyCode]
      return typeof rate === 'number' && rate > 0 ? rate : null
    },
    /** Convert a USD amount to the target currency. Returns null when no rate. */
    convert: (state, getters) => (usdAmount, currencyCode = state.currencyCode) => {
      const rate = getters.rate(currencyCode)
      return rate === null ? null : usdAmount * rate
    },
    /**
     * Convert + format a USD amount for display.
     * Falls back to formatted USD whenever conversion isn't possible,
     * so callers never need their own fallback branch.
     */
    format: (state, getters) => (usdAmount, options = {}) => {
      const target = options.currency || state.currencyCode
      const converted = getters.convert(usdAmount, target)
      if (converted === null) return formatCurrency(usdAmount, 'USD', options)
      return formatCurrency(converted, target, options)
    },
    /** Same as `format`, split into parts for styled price displays. */
    formatParts: (state, getters) => (usdAmount, options = {}) => {
      const target = options.currency || state.currencyCode
      const converted = getters.convert(usdAmount, target)
      if (converted === null) return formatCurrencyParts(usdAmount, 'USD', options)
      return formatCurrencyParts(converted, target, options)
    },
  },
  actions: {
    /**
     * Detect the user's currency and make sure rates are available.
     * Cheap to call repeatedly: fresh cache → no network request at all.
     */
    async initialize({ commit, dispatch, rootState }) {
      const storeTimezone = rootState.user && rootState.user.userData
        ? rootState.user.userData.timezone
        : null
      commit('SET_DETECTION', detectCurrency(storeTimezone))
      await dispatch('ensureRates')
    },
    /**
     * Re-run detection only (e.g. after the user changes their timezone).
     * The cached table already holds every currency, so no refetch is needed.
     */
    syncWithTimezone({ commit, rootState }) {
      const storeTimezone = rootState.user && rootState.user.userData
        ? rootState.user.userData.timezone
        : null
      commit('SET_DETECTION', detectCurrency(storeTimezone))
    },
    async ensureRates({ state, commit }, { force = false } = {}) {
      const cached = readCache()
      if (!force && isFresh(cached)) {
        commit('SET_RATES', { rates: cached.rates, fetchedAt: cached.fetchedAt, provider: cached.provider })
        commit('SET_STATUS', 'ready')
        return
      }
      if (state.status === 'loading' && inflightFetch) {
        await inflightFetch
        return
      }
      commit('SET_STATUS', 'loading')
      inflightFetch = (async () => {
        try {
          const { rates, expiresAt, provider } = await fetchUsdRates()
          const entry = writeCache({ rates, expiresAt, provider })
          commit('SET_RATES', { rates: entry.rates, fetchedAt: entry.fetchedAt, provider: entry.provider })
          commit('SET_STATUS', 'ready')
        } catch (error) {
          if (cached) {
            // All providers down: serve stale rates rather than dropping to USD
            commit('SET_RATES', { rates: cached.rates, fetchedAt: cached.fetchedAt, provider: cached.provider })
            commit('SET_STATUS', 'ready')
          } else {
            commit('SET_STATUS', 'error') // getters fall back to USD display
          }
        } finally {
          inflightFetch = null
        }
      })()
      await inflightFetch
    },
  },
}

/**
 * Vuex plugin: re-detect the currency whenever the logged-in user's profile
 * (which carries the timezone) is written to the store — login, signup,
 * or a timezone change in settings.
 */
export function currencyPlugin(store) {
  store.subscribe(mutation => {
    if (mutation.type === 'user/setUserInformation') {
      store.dispatch('currency/syncWithTimezone')
    }
  })
}
